import React, { useEffect, useState, useRef } from "react";
import { useAccountContext } from "../context/AccountContext";
import { useNavigate, useLocation } from "react-router-dom";
import PromptSpin from "../images/prompts.lottie";
import Logo from "../images/logo_light_grey.png";
import { db } from "../db";
import { getUseDepApi, getAuthorizationToken } from "../api-helper";
import electronBridge from "../utils/electronBridge";
import { isStaffOrCreator } from '../utils/accountTypes';

export default function CosyUI() {
    const accountContext = useAccountContext();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [animations, setAnimations] = useState(false);
    const [comfyuiReady, setComfyuiReady] = useState(false);
    const iframeRef = useRef(null);
    const workflowSent = useRef(false);
    const authSent = useRef(false);
    const url = `https://cosyui.promptus.ai/?accountno=${accountContext.accountDetails.accountNo}`;

    // Get workflow data from navigation state
    const workflowToLoad = location.state?.workflowToLoad;
    const cosyflowTitle = location.state?.cosyflowTitle || "Untitled Workflow";

    useEffect(() => {
        db.settings.where("key").equals("enable_animations").first(setting => {
            setAnimations(setting?.value || false);
        });
    }, []);

    // 🔹 Send workflow to CosyUI iframe when ComfyUI signals it's ready
    useEffect(() => {
        if (comfyuiReady && workflowToLoad && !workflowSent.current && iframeRef.current) {
            console.log("[React_COSYUI] ComfyUI ready - sending workflow:", cosyflowTitle);

            // Small delay to ensure extension is fully initialized
            setTimeout(() => {
                try {
                    iframeRef.current.contentWindow.postMessage({
                        type: "LOAD_WORKFLOW",
                        payload: {
                            workflow: workflowToLoad,
                            title: cosyflowTitle
                        }
                    }, "*");
                    console.log("[React_COSYUI] LOAD_WORKFLOW message sent");
                    workflowSent.current = true;
                } catch (error) {
                    console.error("[React_COSYUI] Failed to send workflow:", error);
                }
            }, 500); // Small delay to ensure extension is initialized
        }
    }, [comfyuiReady, workflowToLoad, cosyflowTitle]);

    // 🔹 Send auth info to CosyUI iframe when ComfyUI signals it's ready
    useEffect(() => {
        if (comfyuiReady && !authSent.current && iframeRef.current) {
            const sendAuthInfo = async () => {
                try {
                    const useDevApi = await getUseDepApi();
                    const apiKey = getAuthorizationToken();
                    const accountNo = accountContext.accountDetails.accountNo;

                    console.log("[React_COSYUI] Sending auth info to CosyUI");

                    iframeRef.current.contentWindow.postMessage({
                        type: "AUTH_INFO",
                        payload: {
                            useDevApi,
                            accountNo,
                            apiKey
                        }
                    }, "*");

                    console.log("[React_COSYUI] AUTH_INFO message sent");
                    authSent.current = true;
                } catch (error) {
                    console.error("[React_COSYUI] Failed to send auth info:", error);
                }
            };

            sendAuthInfo();
        }
    }, [comfyuiReady, accountContext.accountDetails.accountNo]);

    // 🔹 Listen for messages FROM CosyUI (iframe)
    useEffect(() => {
        console.log("[React_COSYUI] Message handler useEffect running");

        function handleMessage(event) {
            console.log("[React_COSYUI] Raw message received:", event.origin, event.data);

            // Accept messages from the cosyui domain
            if (!event.origin.includes('cosyui.promptus.ai')) {
                console.log("[React_COSYUI] Ignoring message from:", event.origin);
                return;
            }

            const data = event.data || {};
            const type = data.type;
            const payload = data.payload;

            console.log("[React_COSYUI] message from CosyUI: ", type, payload);

            // Handle ComfyUI ready signal
            if (type === "comfyui-ready") {
                console.log("[React_COSYUI] ComfyUI is ready");
                setComfyuiReady(true);
            }

            // Handle workflow load confirmation
            if (type === "WORKFLOW_LOADED") {
                console.log("[React_COSYUI] Workflow loaded successfully:", payload?.title);
            }

            if (type === "WORKFLOW_LOAD_ERROR") {
                console.error("[React_COSYUI] Workflow load error:", payload?.error);
            }

            // Debug handler for router acknowledgment
            if (type === "ROUTER_DEBUG") {
                console.log("[React_COSYUI] Router acknowledgment:", payload?.message);
            }

            if (type === "COMFY_SHARE_STARTED") {
                console.log("[React_COSYUI] Processing COMFY_SHARE_STARTED");
                console.log("[React_COSYUI] Incoming payload structure:", {
                    hasData: !!payload?.data,
                    hasCosyflow: !!payload?.data?.cosyflow,
                    cosyflowKeys: payload?.data?.cosyflow ? Object.keys(payload.data.cosyflow) : []
                });

                const incomingCosyflow = payload?.data?.cosyflow;

                if (incomingCosyflow) {
                    const cosyflowInner = {
                        title: incomingCosyflow.title || "",
                        description: incomingCosyflow.description || "",
                        category: incomingCosyflow.tags?.[0] || null,
                        workflow: incomingCosyflow.workflow || {},
                        prompt: incomingCosyflow.prompt || {},
                        assignments: incomingCosyflow.assignments || [],
                        variables: incomingCosyflow.variables || {}
                    };

                    const cosyflowData = {
                        trusted: "0",
                        public: "0",
                        distributed: "0",
                        title: incomingCosyflow.title || "",
                        description: incomingCosyflow.description || "",
                        cosyflow: JSON.stringify(cosyflowInner),
                        category: incomingCosyflow.tags?.[0] || null
                        // Note: uid is intentionally omitted so CosyflowEditor recognizes this as a ComfyUI import
                    };
                    console.log("[React_COSYUI] Compiled Cosyflow Data: ", cosyflowData);
                    console.log("[React_COSYUI] cosyflowInner details:", {
                        hasWorkflow: !!cosyflowInner.workflow,
                        hasPrompt: !!cosyflowInner.prompt,
                        hasAssignments: !!cosyflowInner.assignments,
                        assignmentsLength: cosyflowInner.assignments?.length,
                        hasVariables: !!cosyflowInner.variables,
                        variablesKeys: Object.keys(cosyflowInner.variables || {})
                    });

                    navigate("/cosyflow-editor", { state: { importedCosyflow: cosyflowData, isNewFromComfyUI: true } });
                } else {
                    console.warn("[React_COSYUI] No cosyflow data in payload");
                }
            }
        }

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [navigate]);

    useEffect(() => {
        if (!isStaffOrCreator(accountContext.accountDetails)) {
            navigate('/');
        }
    }, [accountContext.accountDetails]);

    if (!isStaffOrCreator(accountContext.accountDetails)) {
        return null;
    }

    return (
        <div className="w-full h-full relative">
            {/* Title bar overlay - only in Electron to prevent iframe from capturing clicks on window controls */}
            {electronBridge.isElectron && (
                <div
                    className="absolute top-0 left-0 right-0 h-1 z-50"
                    style={{ WebkitAppRegion: 'drag' }}
                />
            )}
            {/* Always render iframe but control visibility */}
            <iframe
                ref={iframeRef}
                src={url}
                className="w-full h-full"
                style={{ display: loading ? 'none' : 'block' }}
                onLoad={() => {
                    console.log('[COSY UI] iframe loaded successfully');
                    setLoading(false);
                }}
                onError={() => {
                    console.error('[COSY UI] iframe failed to load');
                    setLoading(false); // Still hide loader even on error
                }}
            />

            {/* Show loader overlay while loading */}
            {loading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-dark-blue py-32">
                    {animations ?
                        <div className="w-2/3 h-2/3 mx-auto">
                            <lottie-player
                                autoplay
                                loop
                                mode="normal"
                                src={PromptSpin}
                            ></lottie-player>
                        </div>
                    :
                        <img src={Logo} alt={"Promptus Logo"} className="w-2/3 mx-auto animate-pulse" />
                    }
                </div>
            )}
        </div>
    );
}